import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import ga from '../services/analytics';
import { preserveUtmParams } from '../utils/utm';
import { listSharedWithMe } from '../services/shareService';
import { WelcomeOnboardingMobile } from '../components/onboarding/WelcomeOnboardingMobile';
import { WelcomeOnboardingDesktop } from '../components/onboarding/WelcomeOnboardingDesktop';
import {
  getOnboardingLayoutType,
  resolveDemoTrackArtist,
  resolveDemoTrackDisplayName,
} from '../components/onboarding/onboardingConstants';
import { useOnboardingLayout } from '../hooks/useOnboardingLayout';

const WelcomeOnboardingPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { clearNewUserSession, defaultTrackId: configuredDemoTrackId } = useAuth();
  const layout = useOnboardingLayout();
  const viewedRef = useRef(false);

  const [demoTrackId, setDemoTrackId] = useState<string | null>(configuredDemoTrackId);
  const [demoTrackName, setDemoTrackName] = useState<string | null>(null);
  const [demoTrackArtist, setDemoTrackArtist] = useState<string | null>(null);
  const [demoLoading, setDemoLoading] = useState(true);
  const [demoUnavailable, setDemoUnavailable] = useState(false);

  useEffect(() => {
    if (configuredDemoTrackId) {
      setDemoTrackId(configuredDemoTrackId);
    }
  }, [configuredDemoTrackId]);

  useEffect(() => {
    if (viewedRef.current) return;
    viewedRef.current = true;
    const layoutType = getOnboardingLayoutType();
    ga.event('onboarding_viewed', { layout_type: layoutType });
  }, []);

  useEffect(() => {
    let cancelled = false;

    const sleep = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

    const loadDemo = async () => {
      setDemoLoading(true);
      const maxAttempts = 8;
      for (let attempt = 0; attempt < maxAttempts; attempt += 1) {
        if (cancelled) return;
        try {
          const shared = await listSharedWithMe();
          if (cancelled) return;
          const demo =
            (configuredDemoTrackId
              ? shared.find((s) => s.track?.id === configuredDemoTrackId)
              : undefined)
            ?? shared.find((s) => s.track?.isSystemDemo)
            ?? shared[0];
          if (demo?.track?.id) {
            setDemoTrackId(demo.track.id);
            setDemoTrackName(demo.track.name);
            setDemoTrackArtist(demo.track.artist ?? null);
            setDemoUnavailable(false);
            setDemoLoading(false);
            return;
          }
          if (configuredDemoTrackId && attempt >= maxAttempts - 1) {
            setDemoTrackId(configuredDemoTrackId);
            setDemoUnavailable(false);
            setDemoLoading(false);
            return;
          }
        } catch {
          if (cancelled) return;
        }
        if (attempt < maxAttempts - 1) {
          await sleep(1500);
        }
      }
      if (!cancelled) {
        setDemoUnavailable(!configuredDemoTrackId);
        setDemoLoading(false);
      }
    };

    loadDemo();
    return () => {
      cancelled = true;
    };
  }, [configuredDemoTrackId]);

  const finish = useCallback(
    (path: string) => {
      clearNewUserSession();
      navigate(preserveUtmParams(path), { replace: true });
    },
    [clearNewUserSession, navigate]
  );

  const layoutType = () => getOnboardingLayoutType();

  const handleSkip = () => {
    ga.event('onboarding_skipped', { layout_type: layoutType() });
    finish('/dashboard');
  };

  const handleCreate = () => {
    ga.event('onboarding_create_clicked', { layout_type: layoutType() });
    finish('/tracks/new');
  };

  const handleDemo = () => {
    if (demoLoading || demoUnavailable || !demoTrackId) return;
    ga.event('onboarding_demo_clicked', {
      layout_type: layoutType(),
      demo_track_id: demoTrackId,
    });
    clearNewUserSession();
    navigate(preserveUtmParams(`/tracks/${demoTrackId}`), { replace: true });
  };

  const trackName = resolveDemoTrackDisplayName(demoTrackName, t('onboarding.demo.trackNameFallback'));
  const trackArtist = resolveDemoTrackArtist(demoTrackArtist, t('onboarding.demo.trackArtist'));
  const sharedTabLabel = t('dashboard.tabs.sharedWithMe');

  const contentProps = {
    trackName,
    trackArtist,
    sharedTabLabel,
    demoLoading,
    demoUnavailable,
    onSkip: handleSkip,
    onDemo: handleDemo,
    onCreate: handleCreate,
  };

  return layout === 'desktop' ? (
    <WelcomeOnboardingDesktop {...contentProps} active />
  ) : (
    <WelcomeOnboardingMobile {...contentProps} active />
  );
};

export default WelcomeOnboardingPage;
